import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

export interface PartnerOnboardingWizardProps {
  open: boolean;
  apiKeyFull: string;
  organizationName: string;
  onComplete: (webhookUrl: string) => void;
}

const PartnerOnboardingWizard: React.FC<PartnerOnboardingWizardProps> = ({
  open,
  apiKeyFull,
  organizationName,
  onComplete,
}) => {
  const [step, setStep] = useState(0);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(apiKeyFull);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>Welcome, {organizationName}</DialogTitle>
      <DialogContent>
        {step === 0 ? (
          <Stack spacing={2}>
            <Alert severity="warning">
              Copy your API key now — it will not be shown again. Store it securely.
            </Alert>
            <TextField
              fullWidth
              label="API key"
              value={apiKeyFull}
              InputProps={{ readOnly: true }}
              multiline
              minRows={2}
            />
            <Button onClick={copyKey} variant="outlined">
              {copied ? 'Copied!' : 'Copy API key'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              Default scopes: msme:read, carbon:read. Contact support to expand scopes or activate
              billing.
            </Typography>
          </Stack>
        ) : step === 1 ? (
          <Stack spacing={2}>
            <Typography variant="body2">
              Optional: configure a webhook URL to receive loan and application lifecycle events.
            </Typography>
            <TextField
              fullWidth
              label="Webhook URL"
              placeholder="https://your-bank.example.com/webhooks/carbon-intelligence"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
            />
            <Button component={RouterLink} to="/partners/api-documentation" target="_blank">
              Open API documentation
            </Button>
          </Stack>
        ) : (
          <Alert severity="success">
            Partner account is ready. Sign in anytime to view usage, MSME cohorts, and review queues.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {step < 2 ? (
          <Button
            variant="contained"
            onClick={() => {
              if (step === 1) {
                onComplete(webhookUrl);
                setStep(2);
              } else {
                setStep(1);
              }
            }}
          >
            {step === 0 ? 'I saved my key — continue' : 'Finish setup'}
          </Button>
        ) : (
          <Button variant="contained" component={RouterLink} to="/login?type=partner">
            Sign in to partner portal
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PartnerOnboardingWizard;
